import { z } from 'zod';
import { metricContributionsPointSchema, metricInstallSchema, metricPluginsPointSchema, metricSummarySchema, metricTeamActivitySchema, metricTeamContributionsSchema, metricUsersPointSchema, } from '../schemas/metrics.js';
/**
 * Trailing-window query. 30-day default per spec §5.7 headline metric;
 * 90-day ceiling matches the `analytics_daily_*` purge horizon — querying
 * further back risks reading purged rows.
 */
export const metricsWindowQuerySchema = z
    .object({ days: z.coerce.number().int().min(1).max(90).default(30) })
    .strict();
/** `GET /api/metrics/installs?days=…` — top-N plugin installs. */
export const getMetricsInstallsQuerySchema = metricsWindowQuerySchema;
export const getMetricsInstallsResponseSchema = z.object({
    items: z.array(metricInstallSchema),
});
/** `GET /api/metrics/team-activity?days=…` — per-team submission + active-user counters. */
export const getMetricsTeamActivityQuerySchema = metricsWindowQuerySchema;
export const getMetricsTeamActivityResponseSchema = z.object({
    items: z.array(metricTeamActivitySchema),
});
/** `GET /api/metrics/summary` — plugin status snapshot, no query. */
export const getMetricsSummaryResponseSchema = metricSummarySchema;
/**
 * Common window + bucket query for every `/api/metrics/*` time series.
 *
 * `from` / `to` are ISO dates; both optional (the service defaults to the
 * trailing 90 days). Deliberately NOT capped at 90 days like
 * `metricsWindowQuerySchema` above: that ceiling exists because
 * `analytics_events` is purged after 90 days, and none of these series read
 * it. Users and plugins are computed from `users` / `plugins`, contributions
 * from `analytics_daily_contributions`, all of which are retained
 * indefinitely — capping them would hide real history.
 */
export const metricsRangeQuerySchema = z
    .object({
    from: z.string().date().optional(),
    to: z.string().date().optional(),
    grain: z.enum(['week', 'month']).default('week'),
})
    .strict();
/** `GET /api/metrics/users/timeseries` */
export const getMetricsUsersTimeseriesResponseSchema = z.object({
    items: z.array(metricUsersPointSchema),
});
/** `GET /api/metrics/contributions/timeseries` */
export const getMetricsContributionsTimeseriesResponseSchema = z.object({
    items: z.array(metricContributionsPointSchema),
});
/** `GET /api/metrics/contributions/by-team` */
export const getMetricsContributionsByTeamResponseSchema = z.object({
    items: z.array(metricTeamContributionsSchema),
});
/** `GET /api/metrics/plugins/timeseries` */
export const getMetricsPluginsTimeseriesResponseSchema = z.object({
    items: z.array(metricPluginsPointSchema),
});
/**
 * `GET /api/metrics/export.csv` — same window/grain surface as the series
 * routes, no pagination. The response is a `text/csv` attachment, so there is
 * no response schema (the registry entry carries `csv: {}` and Orval skips
 * the JSON client).
 */
export const exportMetricsQuerySchema = metricsRangeQuerySchema;
//# sourceMappingURL=metrics.js.map